import { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { USER_DETAIL } from "@/constants/testIds";
import NextActionBadge from "@/components/NextActionBadge";
import CpsV2Panel from "@/components/CpsV2Panel";
import CpsTrendChart from "@/components/CpsTrendChart";
import AgenticPanel from "@/components/AgenticPanel";
import PurposeToggle from "@/components/PurposeToggle";
import { interpretSendResponse, describeSendError, PROMO_HINT_KEYS } from "@/lib/sendResult";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Send, Zap, Mail, MessageCircle,
  User, Bot, PowerOff, Power, PlayCircle, X, RefreshCw, Info,
  BellOff, Plus, Tag, Check, CheckCheck, AlertTriangle, StickyNote, Users2, Save,
  Link2, Copy, ExternalLink, ShieldCheck,
} from "lucide-react";


const TAB_TEMPLATE_HINTS = {
  welcome: "Warm welcome message; ask what they're preparing for.",
  features: "Highlight one killer feature (adaptive mock tests).",
  social_proof: "Share a short student success story.",
  pricing: "Address pricing objection; mention plans + refund policy.",
  urgency: "Trial ending — soft nudge to subscribe.",
};

// WhatsApp-style delivery ticks for outbound messages.
// - ✓        sent (queued at Meta)
// - ✓✓       delivered (reached the device)
// - ✓✓ blue  read (recipient opened it)
// - ⚠        failed
function WhatsAppTick({ status, readAt, deliveredAt, failureReason }) {
  if (!status) return null;
  const iconCls = "h-3 w-3";
  if (status === "failed") {
    return (
      <span
        title={failureReason ? `Failed: ${failureReason}` : "Delivery failed"}
        className="inline-flex items-center text-red-500 tracking-normal normal-case font-semibold"
      >
        <AlertTriangle className={iconCls} />
      </span>
    );
  }
  if (status === "read") {
    return (
      <span
        title={readAt ? `Read ${new Date(readAt).toLocaleString()}` : "Read"}
        className="inline-flex items-center text-sky-500 tracking-normal normal-case font-semibold"
      >
        <CheckCheck className={iconCls} />
      </span>
    );
  }
  if (status === "delivered") {
    return (
      <span
        title={deliveredAt ? `Delivered ${new Date(deliveredAt).toLocaleString()}` : "Delivered"}
        className="inline-flex items-center text-slate-400 tracking-normal normal-case font-semibold"
      >
        <CheckCheck className={iconCls} />
      </span>
    );
  }
  if (!["sent", "accepted"].includes(status)) return (
    <span title="Delivery uncertain; do not automatically retry"><AlertTriangle className={iconCls} /></span>
  );
  return (
    <span title="Accepted by provider; delivery not confirmed" className="inline-flex items-center text-slate-400 tracking-normal normal-case font-semibold">
      <Check className={iconCls} />
    </span>
  );
}

// v29.15 — Compact copy-to-clipboard chip used by the header IDs strip.
function IdChip({ label, value, testId }) {
  if (!value) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-400 border border-dashed border-slate-200 rounded px-2 py-0.5"
        title={`${label} not yet available`}
      >
        {label} · —
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(String(value));
        toast.success(`${label} ID copied`);
      }}
      title={`${label}: ${value} (click to copy)`}
      className="group inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-slate-100 hover:bg-orange-100 text-slate-700 rounded px-2 py-0.5 border border-slate-200 hover:border-orange-300 transition-colors duration-150"
    >
      <span className="not-italic text-slate-500 group-hover:text-orange-600">{label}</span>
      <span className="text-slate-800 group-hover:text-orange-700 truncate max-w-[10rem]">
        {String(value).length > 12 ? `${String(value).slice(0, 12)}…` : value}
      </span>
      <Copy className="h-2.5 w-2.5 text-slate-400 group-hover:text-orange-600" />
    </button>
  );
}


// v29.15c — "Linked history" pill: surfaces prior accounts that share
// the same phone/email as this user (typically previously-deleted
// accounts by the same person who re-registered). Click to expand;
// each row navigates to that user's profile.
function LinkedHistoryPill({ accounts }) {
  const [open, setOpen] = useState(false);
  const deletedCount = accounts.filter(
    (a) => (a.subscription_status || "").toLowerCase() === "deleted",
  ).length;
  return (
    <div className="relative">
      <button
        type="button"
        data-testid="user-linked-history-pill"
        onClick={() => setOpen((s) => !s)}
        title="Click to view prior accounts that share this phone/email"
        className="group inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest bg-amber-100 hover:bg-amber-200 text-amber-800 rounded px-2 py-0.5 border border-amber-300 transition-colors duration-150"
      >
        <AlertTriangle className="h-2.5 w-2.5" />
        {deletedCount > 0 ? `${deletedCount} prior deleted` : `${accounts.length} linked`}
      </button>
      {open && (
        <div
          data-testid="user-linked-history-panel"
          className="absolute z-30 mt-1 right-0 w-[22rem] bg-white border border-amber-200 rounded-md shadow-xl p-2 max-h-[280px] overflow-y-auto"
        >
          <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 px-2 py-1">
            Prior accounts · same phone or email
          </div>
          {accounts.map((a) => (
            <Link
              key={a.id}
              to={`/users/${a.id}`}
              className="block px-2 py-1.5 rounded hover:bg-amber-50 text-xs"
              data-testid={`linked-account-${a.id}`}
              onClick={() => setOpen(false)}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-800 truncate">
                  {a.name || "—"}
                </span>
                <span
                  className={`text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                    (a.subscription_status || "").toLowerCase() === "deleted"
                      ? "bg-red-100 text-red-700 border border-red-200"
                      : "bg-slate-100 text-slate-700 border border-slate-200"
                  }`}
                >
                  {a.subscription_status || "unknown"}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 font-mono">
                <span>ext {a.external_id || "—"}</span>
                {a.join_date && (
                  <span>· joined {new Date(a.join_date).toLocaleDateString()}</span>
                )}
                {a.deleted_at && (
                  <span>· deleted {new Date(a.deleted_at).toLocaleDateString()}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}


export default function UserDetail() {
  const { id } = useParams();
  const location = useLocation();
  // Preserve caller filter — remembers Statistics → /users?bucket=paid → this detail page
  const backTo = location.state?.from || "/users";
  const backLabel = (() => {
    if (backTo === "/") return "Back to Dashboard";
    if (backTo === "/users") return "All users";
    if (backTo.startsWith("/users?")) return "Back to filtered list";
    if (backTo.startsWith("/users/")) return "Back";
    return "Back";
  })();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState("whatsapp");
  const [body, setBody] = useState("");
  const [purpose, setPurpose] = useState("support");
  const [promoLocked, setPromoLocked] = useState(false);
  const [aiDraft, setAiDraft] = useState(false);
  const sendInFlight = useRef(false);
  const [sendNotice, setSendNotice] = useState(null);
  const [subject, setSubject] = useState("");
  const [inboundOpen, setInboundOpen] = useState(false);
  const [inboundText, setInboundText] = useState("");
  const [labelsMaster, setLabelsMaster] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [dndOpen, setDndOpen] = useState(false);
  const [dndReason, setDndReason] = useState("");
  const [confirmingDndRemove, setConfirmingDndRemove] = useState(false);
  const scrollRef = useRef(null);
  // v29 — Notes editor + Groups strip
  const [userGroups, setUserGroups] = useState([]);
  const [notesEdit, setNotesEdit] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  // v29.12 — Subscription integration (Phase 1)
  const [subLink, setSubLink] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subSending, setSubSending] = useState(false);
  const linkInFlight = useRef(false);
  const [subActivity, setSubActivity] = useState([]);
  const [subStatus, setSubStatus] = useState(null);
  // v29.14 — Conversation / History tab strip
  const [timelineTab, setTimelineTab] = useState("conversation");
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadUser = async () => {
    const { data } = await api.get(`/trial-users/${id}`);
    setUser(data);
    setNotesDraft(data.notes || "");
  };
  const loadUserGroups = async () => {
    try {
      const { data } = await api.get(`/users/${id}/groups`);
      setUserGroups(data.groups || []);
    } catch { setUserGroups([]); }
  };

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await api.patch(`/trial-users/${id}`, { notes: notesDraft });
      toast.success("Notes saved");
      setNotesEdit(false);
      await loadUser();
    } catch { toast.error("Save failed"); }
    finally { setNotesSaving(false); }
  };

  // v29.12 — Subscription integration
  const loadSubActivity = async () => {
    try {
      const [act, st] = await Promise.all([
        api.get(`/trial-users/${id}/subscription/activity`, { params: { limit: 50 } }),
        api.get(`/trial-users/${id}/subscription/status`),
      ]);
      setSubActivity(act.data.events || []);
      setSubStatus(st.data.status || null);
    } catch { /* silent */ }
  };
  useEffect(() => { if (id) loadSubActivity(); }, [id]);

  // v29.14 — User history feed (Groups + promos + sub-page milestones)
  const loadHistory = async () => {
    if (!id) return;
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/trial-users/${id}/history`, { params: { limit: 200 } });
      setHistoryEvents(data.events || []);
    } catch { /* silent */ } finally { setHistoryLoading(false); }
  };
  useEffect(() => {
    if (id && timelineTab === "history") loadHistory();
  }, [id, timelineTab]);

  const generateSubLink = async () => {
    setSubLoading(true);
    try {
      const { data } = await api.post(`/trial-users/${id}/subscription/link`, { channel: "manual" });
      setSubLink(data);
      return data;
    } catch (e) {
      toast.error(describeSendError(e));
      return null;
    } finally { setSubLoading(false); }
  };
  const copySubLink = async () => {
    if (!subLink?.subscription_url) return;
    try {
      await navigator.clipboard.writeText(subLink.subscription_url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };
  const sendSubLink = async (channel) => {
    if (linkInFlight.current) return;
    linkInFlight.current = true;
    setSubSending(true);
    try {
      let link = subLink;
      if (!link) link = await generateSubLink();
      if (!link) return;
      const { data } = await api.post(`/trial-users/${id}/subscription/send`, {
        link_id: link.id, channel,
      });
      if (!data.ok || !data.provider_message_id) return toast.warning("Delivery is uncertain. Check activity before resending. No automatic retry.");
      setSubLink(data.link);
      toast.success("Accepted by provider; delivery is not yet confirmed.");
      loadSubActivity();
    } catch (e) {
      toast.error(describeSendError(e));
    } finally { linkInFlight.current = false; setSubSending(false); }
  };
  const loadMessages = async ({ silent = false } = {}) => {
    const { data } = await api.get(`/trial-users/${id}/messages`);
    setMessages((prev) => {
      // On silent (poll) reload, only auto-scroll if new messages arrived
      // AND user was already near the bottom (avoid yanking them mid-read).
      if (silent) {
        const grew = data.length > prev.length;
        const el = scrollRef.current;
        const nearBottom = el ? (el.scrollHeight - el.scrollTop - el.clientHeight) < 120 : true;
        if (grew && nearBottom) {
          setTimeout(() => el?.scrollTo({ top: 999999, behavior: "smooth" }), 100);
        }
      } else {
        setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 100);
      }
      return data;
    });
  };
  const loadLabels = async () => {
    try {
      const { data } = await api.get("/labels");
      setLabelsMaster(data);
    } catch { /* not fatal */ }
  };

  useEffect(() => {
    loadUser();
    loadMessages();
    loadLabels();
    loadUserGroups();
  }, [id]);

  // ---- Silent polling: every 5s refresh user + messages so cadence sends,
  // AI drafts, inbound webhooks and score updates show up without a manual refresh.
  // Pauses when tab hidden. Skips if a previous poll is still running.
  useEffect(() => {
    if (!id) return;
    let inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        await Promise.all([loadUser(), loadMessages({ silent: true })]);
      } catch { /* silent — never surface poll errors */ }
      finally { inFlight = false; }
    };
    const timer = setInterval(tick, 5000);
    return () => clearInterval(timer);
  }, [id]);

  const draft = async (hint, hintKey) => {
    if (PROMO_HINT_KEYS.includes(hintKey)) { setPurpose("subscription_offer"); setPromoLocked(true); }
    setDraftLoading(true);
    try {
      const { data } = await api.post(`/trial-users/${id}/draft`, { channel, template_hint: hint });
      if (data.escalate) {
        toast.warning("AI recommends escalation — do not send.");
      }
      if (!data.body?.trim()) return toast.warning("AI returned no draft");
      setBody(data.body);
      setAiDraft(true);
      setPurpose("subscription_offer");
      setPromoLocked(true);
      if (channel === "email" && !subject) setSubject("A quick note from PrepHacker");
      toast.success("AI drafted a reply");
    } catch {
      toast.error("Draft failed");
    } finally {
      setDraftLoading(false);
    }
  };

  const send = async (author = "human") => {
    if (sendInFlight.current || draftLoading) return;
    if (!body.trim()) return toast.error("Body is empty");
    sendInFlight.current = true;
    setSending(true);
    setSendNotice(null);
    try {
      const { data } = await api.post(`/trial-users/${id}/send`, {
        channel, body, subject: channel === "email" ? subject : null,
        author: aiDraft ? "ai" : author,
        purpose: aiDraft || author !== "human" || promoLocked ? "subscription_offer" : purpose,
      });
      const r = interpretSendResponse(data);
      setSendNotice(r);
      if (r.status === "sent") {
        setBody(""); setSubject(""); setPurpose("support"); setPromoLocked(false); setAiDraft(false);
        toast.success(r.text);
      } else if (r.status === "failed") toast.error(r.text);
      else toast.warning(r.text);
      await loadMessages().catch(() => {});
    } catch (e) {
      const text = describeSendError(e);
      setSendNotice({ status: "error", tone: "error", text });
      toast.error(text);
      if (e?.response?.status === 409 || e?.response?.status === 502 || !e?.response) loadMessages().catch(() => {});
    } finally {
      sendInFlight.current = false;
      setSending(false);
    }
  };

  const toggleTakeover = async () => {
    await api.patch(`/trial-users/${id}`, { ai_paused: !user.ai_paused });
    toast.success(user.ai_paused ? "AI resumed" : "You took over. AI paused.");
    await loadUser();
  };

  const simulateInbound = async () => {
    if (!inboundText.trim()) return;
    await api.post(`/webhooks/inbound`, {
      channel, body: inboundText, trial_user_id: id,
    });
    setInboundText(""); setInboundOpen(false);
    toast.success("Inbound simulated · AI is re-scoring");
    await loadMessages();
    await loadUser();
  };

  const addTag = async (slug) => {
    const tags = Array.from(new Set([...(user.tags || []), slug]));
    await api.patch(`/trial-users/${id}`, { tags });
    toast.success(`Tag "${slug}" added`);
    setShowTagPicker(false);
    await loadUser();
  };
  const removeTag = async (slug) => {
    const tags = (user.tags || []).filter((t) => t !== slug);
    await api.patch(`/trial-users/${id}`, { tags });
    toast.success(`Tag "${slug}" removed`);
    await loadUser();
  };

  const enableDnd = async () => {
    if (!dndReason.trim()) { toast.error("Reason required"); return; }
    await api.patch(`/trial-users/${id}`, { dnd: true, dnd_reason: dndReason.trim() });
    toast.success("Do Not Contact enabled · AI paused");
    setDndOpen(false); setDndReason("");
    await loadUser();
  };
  const disableDnd = async () => {
    // Two-click confirm — native confirm() is blocked by the preview iframe.
    if (!confirmingDndRemove) {
      setConfirmingDndRemove(true);
      setTimeout(() => setConfirmingDndRemove(false), 5000);
      return;
    }
    setConfirmingDndRemove(false);
    await api.patch(`/trial-users/${id}`, { dnd: false, dnd_reason: null });
    toast.success("Do Not Contact removed");
    await loadUser();
  };

  const [confirmingEscalationClear, setConfirmingEscalationClear] = useState(false);
  const clearEscalation = async () => {
    // Two-click confirm — sticky flag; admins should explicitly resolve.
    if (!confirmingEscalationClear) {
      setConfirmingEscalationClear(true);
      setTimeout(() => setConfirmingEscalationClear(false), 5000);
      return;
    }
    setConfirmingEscalationClear(false);
    await api.patch(`/trial-users/${id}`, { escalated: false });
    toast.success("Escalation cleared");
    await loadUser();
  };

  const [scoring, setScoring] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const recompute = async () => {
    setScoring(true);
    try {
      await api.post(`/trial-users/${id}/recompute-score`);
      toast.success("Score recomputed");
      await loadUser();
      setShowBreakdown(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setScoring(false);
    }
  };

  if (!user) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid={USER_DETAIL.root} className="p-6 lg:p-10 max-w-[1400px]">
      <Link
        to={backTo}
        data-testid="user-detail-back-link"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-4 transition-colors duration-150"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {backLabel}
      </Link>

      {/* Human takeover banner */}
      {user.ai_paused && !user.dnd && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-red-600 flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-red-900">You have taken over this conversation</div>
            <div className="text-xs text-red-700 mt-0.5">
              The AI cadence is paused for {user.name}. No automated messages will be sent until you click &ldquo;Resume AI&rdquo;.
            </div>
          </div>
          <button
            onClick={toggleTakeover}
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 text-white px-3 py-2 rounded-md hover:bg-emerald-500 transition-colors duration-150"
          >
            <Power className="h-3.5 w-3.5" /> Resume AI
          </button>
        </div>
      )}

      {/* DND banner */}
      {user.dnd && (
        <div className="mb-4 bg-slate-900 border border-slate-900 rounded-lg p-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-red-600 flex items-center justify-center shrink-0">
            <BellOff className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-white">Do Not Contact is ON</div>
            <div className="text-xs text-slate-300 mt-0.5">
              AI & cadence are frozen for {user.name}. Reason: <b>{user.dnd_reason || "not specified"}</b>.
              Messaging is blocked while Do Not Contact is enabled.
            </div>
          </div>
          <button
            data-testid={USER_DETAIL.dndToggle}
            onClick={disableDnd}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors duration-150 ${confirmingDndRemove ? "bg-red-600 text-white hover:bg-red-500" : "bg-white text-slate-900 hover:bg-slate-100"}`}
            title={confirmingDndRemove ? "Click again to confirm — AI will resume" : "Remove Do Not Contact flag"}
          >
            {confirmingDndRemove ? "Click to confirm" : "Remove DND"}
          </button>
        </div>
      )}

      {/* Escalation banner — v27 */}
      {user.escalated && (
        <div
          data-testid="user-escalation-banner"
          className="mb-4 bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-center gap-3 shadow-sm"
        >
          <div className="h-9 w-9 rounded-md bg-red-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-red-900 flex items-center gap-2">
              This user has been escalated
              <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-600 text-white">Sticky</span>
            </div>
            <div className="text-xs text-red-800 mt-0.5">
              {user.ai_paused
                ? "AI is currently paused for this user. Human agent should take over."
                : "AI is still drafting messages — auto-pause on escalation is OFF in AI Controls. New AI drafts are flagged with a red [ESCALATED] chip."}
              {user.escalated_at && (
                <> · Escalated at <b>{new Date(user.escalated_at).toLocaleString()}</b></>
              )}
            </div>
          </div>
          <button
            data-testid="user-clear-escalation-btn"
            onClick={clearEscalation}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors duration-150 ${
              confirmingEscalationClear
                ? "bg-red-600 text-white hover:bg-red-500"
                : "bg-white text-red-900 border border-red-300 hover:bg-red-100"
            }`}
            title={confirmingEscalationClear ? "Click again to confirm" : "Clear the escalation flag (admin action)"}
          >
            {confirmingEscalationClear ? "Click to confirm" : "Clear escalation"}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-heading text-2xl font-extrabold tracking-tighter">{user.name}</h1>
              <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${
                user.subscription_status === "trial" ? "bg-orange-50 text-orange-700 border-orange-200" :
                user.subscription_status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                "bg-slate-100 text-slate-600 border-slate-200"
              }`}>{user.subscription_status}</span>
              {user.escalated && (
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border bg-red-50 text-red-700 border-red-200">
                  Escalated
                </span>
              )}
            </div>
            <div className="text-sm text-slate-600 space-y-0.5">
              <div>{user.email} · {user.phone}</div>
              <div>{user.exam} · {user.city}, {user.state}, {user.country}</div>
              <div className="text-xs text-slate-500">Joined {new Date(user.join_date).toLocaleString()}</div>
              {/* v29.15 — All IDs, one glance, one click to copy */}
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 mt-2 border-t border-dashed border-slate-200"
                data-testid="user-ids-strip"
              >
                <IdChip label="Connect" value={user.id} testId="user-id-connect" />
                <IdChip label="Master DB" value={user.external_id} testId="user-id-external" />
                <IdChip label="Subscription" value={subStatus?.external_subscription_id} testId="user-id-ext-sub" />
                {(user.linked_prior_accounts || []).length > 0 && (
                  <LinkedHistoryPill accounts={user.linked_prior_accounts} />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div data-testid={USER_DETAIL.scoreBadge} className="flex items-center gap-2 bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-md px-3 py-2">
              <Zap className="h-4 w-4 text-orange-500" />
              <div>
                <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Conversion</div>
                <div className="font-heading font-extrabold text-xl tabular text-orange-600">{user.conversion_score}</div>
              </div>
              <button onClick={() => setShowBreakdown((s) => !s)} className="ml-1 text-slate-500 hover:text-slate-900" title="Why this score?">
                <Info className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={recompute}
                disabled={scoring}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-orange-600 disabled:opacity-60"
                title="Recompute hybrid score"
              >
                <RefreshCw className={`h-3 w-3 ${scoring ? "animate-spin" : ""}`} /> Rescore
              </button>
            </div>
            <button
              data-testid={USER_DETAIL.takeoverToggle}
              onClick={toggleTakeover}
              disabled={user.dnd}
              className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                user.ai_paused
                  ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-500"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
              title={user.dnd ? "Remove DND first to resume AI" : ""}
            >
              {user.ai_paused ? <><Power className="h-3.5 w-3.5" /> Resume AI</> : <><PowerOff className="h-3.5 w-3.5" /> Take Over</>}
            </button>
            {!user.dnd && (
              <button
                onClick={() => setDndOpen(true)}
                className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors duration-150"
                title="Mark this user as Do Not Contact"
              >
                <BellOff className="h-3.5 w-3.5" /> Mark DND
              </button>
            )}
          </div>
        </div>

        {/* Score breakdown */}
        {showBreakdown && user.score_breakdown && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
              How the score of {user.conversion_score} was computed
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {Object.entries(user.score_breakdown).map(([k, b]) => (
                <div key={k} className="border border-slate-200 rounded-md p-3">
                  <div className="font-semibold text-slate-700 capitalize">{k.replace("_", " ")}</div>
                  <div className="tabular text-slate-500 mt-0.5">
                    Value: <b>{b.value}</b>
                    {b.weight !== undefined && <> · Weight: {Math.round((b.weight || 0) * 100)}%</>}
                  </div>
                  <div className="tabular text-orange-600 font-semibold mt-1">Contribution: {b.contribution}</div>
                  {b.reply_count !== undefined && (
                    <div className="text-[10px] text-slate-500 mt-1">{b.reply_count} reply · avg len {b.avg_reply_len}</div>
                  )}
                  {b.hours_since_login !== undefined && b.hours_since_login !== null && (
                    <div className="text-[10px] text-slate-500 mt-1">last login {b.hours_since_login}h ago</div>
                  )}
                  {b.cohort_cr_pct !== undefined && (
                    <div className="text-[10px] text-slate-500 mt-1">{b.exam} baseline CR: {b.cohort_cr_pct}%</div>
                  )}
                  {k === "labels" && Array.isArray(b.details) && b.details.length > 0 && (
                    <div className="text-[10px] text-slate-600 mt-1 space-y-0.5">
                      {b.details.map((d) => (
                        <div key={d.slug} className="flex justify-between">
                          <span className="font-mono">{d.slug}</span>
                          <span className={`font-mono font-semibold ${d.weight > 0 ? "text-emerald-600" : d.weight < 0 ? "text-red-600" : "text-slate-400"}`}>
                            {d.weight > 0 ? `+${d.weight}` : d.weight}
                          </span>
                        </div>
                      ))}
                      {b.raw !== undefined && b.raw !== b.value && (
                        <div className="text-[9px] text-slate-500 pt-0.5 border-t border-slate-100">Raw {b.raw > 0 ? `+${b.raw}` : b.raw} → capped at ±30</div>
                      )}
                    </div>
                  )}
                  {k === "web_subscription" && (
                    <div className="text-[10px] text-slate-600 mt-1 space-y-0.5">
                      {b.winning_event ? (
                        <>
                          <div className="flex justify-between">
                            <span className="font-mono">{b.winning_event.event_type}</span>
                            <span className={`font-mono font-semibold ${b.value > 0 ? "text-emerald-600" : b.value < 0 ? "text-red-600" : "text-slate-400"}`}>
                              {b.value > 0 ? `+${b.value}` : b.value}
                            </span>
                          </div>
                          <div className="text-[9px] text-slate-500">
                            strongest of {b.considered_count} event{b.considered_count === 1 ? "" : "s"} in last {b.window_days}d · capped at ±15
                          </div>
                        </>
                      ) : (
                        <div className="text-[9px] text-slate-500 italic">No sub-page activity in last {b.window_days || 14}d</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- CPS V2 — Shadow scoring intelligence (Stage 5) ---- */}
        <CpsV2Panel userId={id} legacyScore={user.conversion_score} />

        {/* Tags */}
        <div data-testid={USER_DETAIL.tagsChips} className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mr-1">Tags</span>
          {(user.tags || []).map((t) => (
            <span key={t} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 pl-2 pr-1 py-0.5 rounded font-semibold uppercase tracking-widest inline-flex items-center gap-1">
              {t}
              <button
                onClick={() => removeTag(t)}
                className="text-indigo-400 hover:text-red-600 ml-0.5"
                title={`Remove "${t}"`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <div className="relative">
            <button
              data-testid={USER_DETAIL.addTagBtn}
              onClick={() => setShowTagPicker((s) => !s)}
              className="text-[10px] inline-flex items-center gap-1 border border-dashed border-slate-300 text-slate-600 hover:text-slate-900 hover:border-slate-500 px-2 py-0.5 rounded font-semibold uppercase tracking-widest"
            >
              <Plus className="h-3 w-3" /> Add tag
            </button>
            {showTagPicker && (
              <div className="absolute top-6 left-0 z-20 bg-white border border-slate-200 rounded-md shadow-lg w-64 max-h-72 overflow-y-auto">
                <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                  Apply a label
                  <button onClick={() => setShowTagPicker(false)}><X className="h-3 w-3" /></button>
                </div>
                {labelsMaster.filter((l) => !(user.tags || []).includes(l.slug)).length === 0 && (
                  <div className="p-3 text-xs text-slate-500">No more labels to add.</div>
                )}
                {labelsMaster.filter((l) => !(user.tags || []).includes(l.slug)).map((l) => (
                  <button
                    key={l.id}
                    onClick={() => addTag(l.slug)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                  >
                    <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                      <Tag className="h-3 w-3 text-slate-500" /> {l.name}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{l.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {(!user.tags || user.tags.length === 0) && (
            <span className="text-xs text-slate-400 ml-2">No tags yet — apply manually or wait for AI auto-tag.</span>
          )}
        </div>

        {/* v29 — Groups membership strip */}
        <div data-testid="user-groups-strip" className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mr-1 inline-flex items-center gap-1">
            <Users2 className="h-3 w-3" /> Groups
          </span>
          {userGroups.length === 0 ? (
            <span className="text-xs text-slate-400">
              Not in any group.{" "}
              <Link to="/groups" className="text-indigo-600 hover:underline">Manage groups →</Link>
            </span>
          ) : (
            userGroups.map((g) => (
              <Link
                key={g.id}
                to="/groups"
                title={`View group: ${g.name}${g.system ? " (system)" : ""}${g.auto ? " · auto" : " · static"}`}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border bg-${g.color || "slate"}-50 text-${g.color || "slate"}-700 border-${g.color || "slate"}-200 hover:brightness-95`}
              >
                <span className={`h-1.5 w-1.5 rounded-full bg-${g.color || "slate"}-500`}></span>
                {g.name}
                {g.system && <span className="ml-1 opacity-60">(sys)</span>}
              </Link>
            ))
          )}
        </div>

        {/* v29.12 — Subscription card (Phase 1: link + copy + send) */}
        {(user.subscription_status || "").toLowerCase() === "deleted" ? (
          <div
            data-testid="user-subscription-card-deleted"
            className="mt-4 bg-red-50/70 border border-red-200 rounded-md p-3"
          >
            <div className="text-[10px] font-semibold uppercase tracking-widest text-red-800 inline-flex items-center gap-1 mb-1">
              <AlertTriangle className="h-3 w-3" /> Subscription link unavailable
            </div>
            <div className="text-xs text-red-900/90">
              This account is marked <strong>deleted</strong> in the master DB. Any subscription
              link generated here would be rejected by the subscription page with
              <em> &ldquo;This subscription link is invalid or has expired.&rdquo;</em> Restore the account
              upstream first, then re-sync.
            </div>
          </div>
        ) : (
        <div data-testid="user-subscription-card" className="mt-4 bg-emerald-50/60 border border-emerald-200 rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800 inline-flex items-center gap-1">
              <Link2 className="h-3 w-3" /> Subscription link
              <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700/70 normal-case tracking-normal">
                <ShieldCheck className="h-3 w-3" /> personalised
              </span>
            </div>
            {subLink?.status && (
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-900/70">
                {subLink.status === "generated" ? "Ready to send" :
                 subLink.status === "sent" ? "Sent" :
                 subLink.status === "opened" ? "Opened by user" :
                 subLink.status === "purchased" ? "Purchased" : subLink.status}
              </span>
            )}
          </div>
          {!subLink ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-emerald-900/80 flex-1">
                Generate this user&apos;s personalised subscription link and send it via WhatsApp or email.
              </span>
              <button
                data-testid="user-sub-generate-btn"
                onClick={generateSubLink}
                disabled={subLoading}
                className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-500 px-2.5 py-1 rounded disabled:opacity-60"
              >
                <RefreshCw className={`h-3 w-3 ${subLoading ? "animate-spin" : ""}`} />
                Show subscription link
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded px-2 py-1.5 mb-2 overflow-hidden">
                <code data-testid="user-sub-url" className="flex-1 text-xs text-slate-800 font-mono truncate">
                  {subLink.subscription_url}
                </code>
                <button
                  data-testid="user-sub-copy-btn"
                  onClick={copySubLink}
                  className="text-slate-500 hover:text-emerald-700 shrink-0"
                  title="Copy link"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a
                  data-testid="user-sub-open-btn"
                  href={subLink.subscription_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-500 hover:text-emerald-700 shrink-0"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  data-testid="user-sub-send-wa-btn"
                  onClick={() => sendSubLink("whatsapp")}
                  disabled={subSending}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-500 px-2.5 py-1 rounded disabled:opacity-60"
                >
                  <MessageCircle className="h-3 w-3" /> Send via WhatsApp
                </button>
                <button
                  data-testid="user-sub-send-email-btn"
                  onClick={() => sendSubLink("email")}
                  disabled={subSending}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white text-emerald-800 border border-emerald-300 hover:bg-emerald-50 px-2.5 py-1 rounded disabled:opacity-60"
                >
                  <Mail className="h-3 w-3" /> Send via Email
                </button>
                {(subLink.promo_code || subLink.cadence_id) && (
                  <span className="text-[10px] text-emerald-900/70 ml-1">
                    {subLink.promo_code && <>promo <b>{subLink.promo_code}</b></>}
                    {subLink.promo_code && subLink.cadence_id && " · "}
                    {subLink.cadence_id && <>cadence <b>{subLink.cadence_id.slice(0, 6)}…</b></>}
                  </span>
                )}
              </div>
              {subLink.sent_at && (
                <div className="text-[10px] text-emerald-900/70 mt-1.5">
                  Last sent via <b>{subLink.channel}</b> · {new Date(subLink.sent_at).toLocaleString()}
                </div>
              )}
            </>
          )}
          {/* Reconciled status pill + activity timeline */}
          {(subStatus?.is_subscription || subActivity.length > 0) && (
            <div className="mt-3 pt-3 border-t border-emerald-200/70">
              {subStatus?.is_subscription && (
                <div data-testid="user-sub-status-pill" className="mb-2 inline-flex items-center gap-2 bg-emerald-100 text-emerald-900 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded">
                  <CheckCheck className="h-3 w-3" />
                  {subStatus.active_plan_label || "Subscribed"}
                  {subStatus.subscription_expiry_date && (
                    <span className="opacity-70 normal-case tracking-normal">
                      · expires {new Date(subStatus.subscription_expiry_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
              {subActivity.length > 0 && (
                <ol data-testid="user-sub-activity" className="text-xs space-y-1">
                  {subActivity.slice(0, 8).map((ev) => (
                    <li key={ev.event_id || ev.id} className="flex items-start gap-2">
                      <span className="text-[10px] text-slate-500 tabular-nums shrink-0 mt-0.5">
                        {new Date(ev.timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="font-semibold text-emerald-900">
                        {ev.event_type?.replace(/_/g, " ").toLowerCase()}
                      </span>
                      {ev.metadata?.promo_code && (
                        <span className="text-[10px] text-emerald-800/70 font-mono">
                          {ev.metadata.promo_code}
                        </span>
                      )}
                      {ev.metadata?.amount && (
                        <span className="text-[10px] text-emerald-900 tabular-nums">
                          ₹{ev.metadata.amount}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
        )}

        {/* v29 — Admin notes card (visible to all agents) */}
        <div data-testid="user-notes-card" className="mt-4 bg-amber-50/50 border border-amber-200 rounded-md p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-800 inline-flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> Admin notes
            </div>
            {!notesEdit ? (
              <button
                data-testid="user-notes-edit-btn"
                onClick={() => { setNotesDraft(user.notes || ""); setNotesEdit(true); }}
                className="text-[10px] font-semibold text-amber-800 hover:text-amber-900 hover:underline"
              >
                {user.notes ? "Edit" : "Add note"}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setNotesEdit(false); setNotesDraft(user.notes || ""); }}
                  className="text-[10px] font-semibold text-slate-500 hover:text-slate-800"
                >Cancel</button>
                <button
                  data-testid="user-notes-save-btn"
                  onClick={saveNotes}
                  disabled={notesSaving}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-600 text-white hover:bg-amber-500 px-2 py-1 rounded disabled:opacity-60"
                >
                  <Save className="h-3 w-3" /> Save
                </button>
              </div>
            )}
          </div>
          {notesEdit ? (
            <textarea
              data-testid="user-notes-textarea"
              rows={3}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Free-text notes about this user (visible to all agents). Also carried in from bulk imports."
              className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white focus:border-amber-500 outline-none"
            />
          ) : user.notes ? (
            <div data-testid="user-notes-body" className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
              {user.notes}
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic">
              No notes yet. Batch import notes and per-user notes will appear here.
            </div>
          )}
        </div>
      </div>

      {/* Chat + composer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg flex flex-col">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                data-testid="user-detail-tab-conversation"
                onClick={() => setTimelineTab("conversation")}
                className={`text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded transition-colors ${
                  timelineTab === "conversation"
                    ? "text-orange-700 bg-orange-50"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Conversation
              </button>
              <button
                data-testid="user-detail-tab-history"
                onClick={() => setTimelineTab("history")}
                className={`text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded transition-colors ${
                  timelineTab === "history"
                    ? "text-orange-700 bg-orange-50"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                History
              </button>
            </div>
            {timelineTab === "conversation" && (
              <button
                data-testid={USER_DETAIL.simulateInboundBtn}
                onClick={() => setInboundOpen(true)}
                className="text-[11px] font-semibold text-indigo-600 hover:underline inline-flex items-center gap-1"
              >
                <PlayCircle className="h-3.5 w-3.5" /> Simulate inbound reply
              </button>
            )}
          </div>

          {timelineTab === "history" ? (
            <div
              data-testid="user-detail-history-timeline"
              className="flex-1 p-5 space-y-2 overflow-y-auto max-h-[520px] min-h-[300px]"
            >
              {historyLoading ? (
                <div className="text-center text-sm text-slate-500 py-10">Loading history…</div>
              ) : historyEvents.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-10 border border-dashed border-slate-200 rounded-md">
                  No history yet. Group changes, promo shares, and sub-page milestones will appear here.
                </div>
              ) : (
                historyEvents.map((ev, i) => {
                  const HISTORY_META = {
                    user_added_to_group:          { color: "emerald", label: "Group Added",    icon: "＋" },
                    user_removed_from_group:      { color: "red",     label: "Group Removed",  icon: "－" },
                    subscription_link_generated:  { color: "indigo",  label: "Promo Sent",     icon: "🔗" },
                    PAGE_OPENED:                  { color: "sky",     label: "Sub Page Opened", icon: "◉" },
                    PROMO_APPLIED:                { color: "amber",   label: "Promo Applied",  icon: "%" },
                    PAYMENT_SUCCESS:              { color: "green",   label: "Payment",        icon: "✓" },
                    SUBSCRIPTION_ACTIVATED:       { color: "green",   label: "Activated",      icon: "★" },
                  };
                  const meta = HISTORY_META[ev.kind] || { color: "slate", label: ev.kind, icon: "•" };
                  return (
                    <div
                      key={i}
                      data-testid={`user-detail-history-row-${i}`}
                      className="flex gap-3 items-start py-2 border-b border-slate-100 last:border-0"
                    >
                      <div className={`h-6 w-6 shrink-0 rounded-full bg-${meta.color}-100 text-${meta.color}-700 flex items-center justify-center text-xs font-bold`}>
                        {meta.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-800 truncate">{ev.title}</div>
                          <div className="text-[10px] tabular text-slate-500 shrink-0" title={ev.at}>
                            {ev.at ? new Date(ev.at).toLocaleString(undefined, {
                              month: "short", day: "numeric",
                              hour: "numeric", minute: "2-digit",
                            }) : ""}
                          </div>
                        </div>
                        {ev.subtitle && (
                          <div className="text-[11px] text-slate-500 mt-0.5">{ev.subtitle}</div>
                        )}
                        {(ev.actor_name || ev.actor) && (
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            by {ev.actor_name || (ev.actor === "user" ? "user" : ev.actor)}
                          </div>
                        )}
                        {ev.details && Object.keys(ev.details).some((k) => ev.details[k]) && (
                          <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-1">
                            {Object.entries(ev.details).filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                              <span key={k} className="bg-slate-100 rounded px-1.5 py-0.5">
                                <span className="text-slate-400">{k}:</span> <span className="font-mono">{String(v).slice(0, 40)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
          <div ref={scrollRef} data-testid={USER_DETAIL.chatTimeline} className="flex-1 p-5 space-y-3 overflow-y-auto max-h-[520px] min-h-[300px]">
            {messages.length === 0 && (
              <div className="text-center text-sm text-slate-500 py-10 border border-dashed border-slate-200 rounded-md">
                No conversation yet. Cadence auto-messaging or manual drafts will appear here.
              </div>
            )}
            {messages.map((m) => {
              const isInbound = m.direction === "inbound";
              const cls =
                m.author === "ai" ? "chat-ai" : m.author === "user" ? "chat-user" : "chat-human";
              const mode = m.mode || m.ai_meta?.mode;
              const modeBadge = m.author === "ai" && mode ? (
                mode === "template" ? { label: "Template", cls: "bg-slate-100 text-slate-700 border-slate-200", title: "Sent as-is from template. No AI tokens spent." } :
                mode === "ai_fallback" ? { label: "AI fallback", cls: "bg-amber-100 text-amber-800 border-amber-300", title: "Template render failed or was empty — AI drafted this as a safety net." } :
                { label: "AI draft", cls: "bg-purple-100 text-purple-800 border-purple-200", title: "AI drafted this using the template as a hint." }
              ) : null;
              // v27: derived [ESCALATED] chip on AI outbound messages sent after user was flagged.
              const showEscalationChip =
                m.author === "ai" &&
                !isInbound &&
                user?.escalated &&
                user?.escalated_at &&
                m.created_at &&
                new Date(m.created_at) >= new Date(user.escalated_at);
              return (
                <div key={m.id} className={`flex ${isInbound ? "justify-start" : "justify-end"}`}>
                  <div className={`${cls} border rounded-lg px-4 py-2.5 max-w-[75%] shadow-sm`}>
                    <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500 flex-wrap">
                      {m.author === "ai" ? <Bot className="h-3 w-3 text-indigo-500" /> :
                       m.author === "user" ? <User className="h-3 w-3 text-orange-500" /> :
                       <User className="h-3 w-3 text-slate-500" />}
                      {m.author}
                      <span className="text-slate-300">·</span>
                      {m.channel === "whatsapp" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                      {m.channel}
                      <span className="text-slate-300">·</span>
                      <span
                        className="tabular"
                        title={new Date(m.created_at).toLocaleString()}
                      >
                        {new Date(m.created_at).toLocaleString(undefined, {
                          month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit",
                        })}
                      </span>
                      {!isInbound && m.channel === "whatsapp" && (
                        <WhatsAppTick status={m.status} readAt={m.read_at} deliveredAt={m.delivered_at} failureReason={m.failure_reason} />
                      )}
                      {modeBadge && (
                        <span
                          title={modeBadge.title}
                          className={`ml-1 inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] tracking-normal normal-case font-semibold ${modeBadge.cls}`}
                        >
                          {modeBadge.label}
                        </span>
                      )}
                      {showEscalationChip && (
                        <span
                          data-testid={`msg-escalated-chip-${m.id}`}
                          className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[9px] tracking-widest font-bold uppercase bg-red-600 text-white border-red-700"
                          title="This AI draft was created AFTER the user was escalated. Auto-pause on escalation is OFF."
                        >
                          <AlertTriangle className="h-2.5 w-2.5" /> Escalated
                        </span>
                      )}
                    </div>
                    {m.subject && <div className="text-xs font-semibold text-slate-800 mb-1">{m.subject}</div>}
                    <div className="text-sm whitespace-pre-wrap text-slate-900">{m.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {/* Composer */}
          <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-lg">
            <div data-testid={USER_DETAIL.channelToggle} className="inline-flex bg-white border border-slate-200 rounded-md p-0.5 mb-3">
              {["whatsapp", "email"].map((c) => (
                <button
                  key={c}
                  onClick={() => setChannel(c)}
                  className={`px-3 py-1 text-xs font-semibold rounded transition-colors duration-150 ${
                    channel === c ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {c === "whatsapp" ? <><MessageCircle className="inline h-3 w-3 mr-1" /> WhatsApp</> :
                                       <><Mail className="inline h-3 w-3 mr-1" /> Email</>}
                </button>
              ))}
            </div>
            <PurposeToggle value={purpose} onChange={setPurpose} supportLocked={promoLocked} />

            {channel === "email" && (
              <input
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full mb-2 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              />
            )}

            <textarea
              data-testid={USER_DETAIL.messageInput}
              rows={3}
              value={body}
              onChange={(e) => { setBody(e.target.value); if (!e.target.value.trim()) { setPromoLocked(false); setAiDraft(false); } }}
              placeholder={`Write a ${channel} message, or click "AI Draft" for suggestions…`}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
            />
            {sendNotice && (
              <div
                data-testid="composer-send-notice"
                data-status={sendNotice.status}
                className={`mt-2 text-xs px-3 py-2 rounded-md border ${
                  sendNotice.tone === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                  sendNotice.tone === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" :
                  "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                {sendNotice.text}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                data-testid={USER_DETAIL.draftBtn}
                onClick={() => draft()}
                disabled={draftLoading}
                className="inline-flex items-center gap-2 bg-indigo-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-indigo-500 transition-colors duration-150 disabled:opacity-60"
              >
                <Sparkles className={`h-3.5 w-3.5 ${draftLoading ? "animate-pulse" : ""}`} />
                {draftLoading ? "Drafting…" : "AI Draft"}
              </button>
              <div className="hidden sm:flex items-center gap-1 text-[10px] text-slate-500">
                {Object.keys(TAB_TEMPLATE_HINTS).map((k) => (
                  <button
                    key={k}
                    data-testid={`composer-hint-${k}`}
                    onClick={() => draft(TAB_TEMPLATE_HINTS[k], k)}
                    disabled={draftLoading}
                    className="px-2 py-1 border border-slate-200 rounded hover:bg-white transition-colors duration-150"
                  >
                    {k}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <button
                data-testid={USER_DETAIL.sendBtn}
                onClick={() => send("human")}
                disabled={sending || draftLoading || !body.trim()}
                className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-4 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
              >
                <Send className="h-3.5 w-3.5" />
                {aiDraft ? "Send AI draft" : "Send as Human"}
              </button>
              <button
                data-testid="user-send-ai-btn"
                onClick={() => send("ai")}
                disabled={sending || draftLoading || !body.trim()}
                className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-800 transition-colors duration-150 disabled:opacity-60"
                title="Send using AI voice (recorded as author=ai)"
              >
                <Bot className="h-3.5 w-3.5" />
                Send as AI
              </button>
            </div>
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Trial timer</div>
            <div className="font-heading font-extrabold text-4xl tabular text-orange-600">
              {user.trial_hours_left ?? "—"}h
            </div>
            <div className="text-xs text-slate-500 mt-1">until trial expires</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">AI status</div>
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${user.dnd ? "bg-red-500" : user.ai_paused ? "bg-slate-400" : "bg-emerald-500"}`} />
              <div className="text-sm font-semibold">
                {user.dnd ? "Frozen (Do Not Contact)" : user.ai_paused ? "Paused (Human control)" : "Active (AI messaging)"}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              {user.dnd
                ? "DND overrides all automation. Remove DND to allow AI or cadence to run again."
                : "When paused, cadence auto-sends are stopped for this user. Resume any time."}
            </p>
            <NextActionBadge userId={id} refreshKey={user.updated_at || 0} />
          </div>
          <AgenticPanel userId={id} refreshKey={user.updated_at || 0} />
          <CpsTrendChart userId={id} refreshKey={user.updated_at || 0} />
        </div>
      </div>

      {dndOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDndOpen(false)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
                <BellOff className="h-4 w-4 text-red-500" /> Mark as Do Not Contact
              </h3>
              <button onClick={() => setDndOpen(false)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              This will <b>pause AI</b>, <b>skip all future cadence steps</b> and add a <b>dnd</b> tag.
              Use when the user has explicitly asked to stop being contacted, or for compliance reasons.
            </p>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Reason (required)</label>
            <textarea
              data-testid={USER_DETAIL.dndReasonInput}
              rows={3}
              placeholder="e.g., User replied STOP · legal complaint · unsubscribed via email"
              value={dndReason}
              onChange={(e) => setDndReason(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-red-500 outline-none transition-colors duration-150 mb-3"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setDndOpen(false)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button
                onClick={enableDnd}
                className="text-xs px-3 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors duration-150"
              >
                Confirm DND
              </button>
            </div>
          </div>
        </div>
      )}

      {inboundOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setInboundOpen(false)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg tracking-tight">Simulate inbound reply</h3>
              <button onClick={() => setInboundOpen(false)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              Dev-only. In production, replies arrive automatically via Meta WhatsApp Cloud webhook or IMAP.
            </p>
            <textarea
              rows={4}
              placeholder={`Reply as ${user.name}…`}
              value={inboundText}
              onChange={(e) => setInboundText(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-indigo-500 outline-none transition-colors duration-150 mb-3"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setInboundOpen(false)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={simulateInbound} className="text-xs px-3 py-2 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors duration-150">Send inbound</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
